import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

// Public-ish overview that works even when not logged in by using static fallback data.
const STATIC_OVERVIEW = [
  { order: 1, title: "Foundations of Peer Support", domain: "Recovery & Identity", hours: 3 },
  { order: 2, title: "Recovery & Wellness: SAMHSA's 10 Guiding Principles", domain: "Recovery & Identity", hours: 4 },
  { order: 3, title: "Ethics, Boundaries & the NC Code of Conduct", domain: "Ethics & Professional Practice", hours: 4 },
  { order: 4, title: "Cultural Humility", domain: "Cultural & Equity Practice", hours: 3 },
  { order: 5, title: "Trauma-Informed Peer Practice", domain: "Clinical Foundations", hours: 4 },
  { order: 6, title: "Listening, Reflection & Motivational Interviewing Basics", domain: "Communication Skills", hours: 4 },
  { order: 7, title: "Crisis, Safety & Suicide Prevention", domain: "Clinical Foundations", hours: 4 },
  { order: 8, title: "Co-occurring Mental Health & Substance Use", domain: "Clinical Foundations", hours: 4 },
  { order: 9, title: "Documentation, HIPAA & 42 CFR Part 2", domain: "Ethics & Professional Practice", hours: 3 },
  { order: 10, title: "Group Facilitation", domain: "Communication Skills", hours: 3 },
  { order: 11, title: "Advocacy & Systems Navigation in North Carolina", domain: "Systems & Advocacy", hours: 2 },
  { order: 12, title: "Self-Care, Supervision & Sustaining the Work", domain: "Professional Development", hours: 2 },
];

export default function CurriculumOverview() {
  const { user } = useAuth();
  const [modules, setModules] = useState(STATIC_OVERVIEW);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/curriculum");
        setModules(data.modules);
      } catch {}
    })();
  }, [user]);

  const totalHours = modules.reduce((s, m) => s + (m.hours || 0), 0);

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16" data-testid="curriculum-overview">
      <div className="label-eyebrow mb-3">The full curriculum · {modules.length} modules · {totalHours} hours</div>
      <h1 className="font-serif text-6xl text-ink leading-[1.05] mb-6">
        Twelve modules. Forty hours.<br />
        <span className="italic text-terracotta">One discipline.</span>
      </h1>
      <p className="text-lg text-muted-foreground max-w-[60ch] leading-relaxed mb-16">
        The full curriculum is taught in service of the conversations that actually happen.
        Each module is anchored in clinical evidence, North Carolina standards, and the
        lived reality of peer practice.
      </p>

      <ol className="border-t border-stone">
        {modules.map((m) => (
          <li key={m.order} className="border-b border-stone py-7 grid grid-cols-1 md:grid-cols-[80px_1fr_140px] gap-6 items-start" data-testid={`overview-module-${m.order}`}>
            <div className="font-serif text-4xl text-stone">{String(m.order).padStart(2, "0")}</div>
            <div>
              <div className="font-serif text-2xl text-ink leading-tight">{m.title}</div>
              <div className="label-eyebrow mt-2">{m.domain}</div>
              {m.summary && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{m.summary}</p>}
            </div>
            <div className="text-right">
              <div className="font-serif text-3xl text-ink">{m.hours}h</div>
              <div className="label-eyebrow">Instructional</div>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-16 text-center">
        {!user ? (
          <Link to="/register" className="btn-primary" data-testid="overview-cta-enroll">Begin training</Link>
        ) : (
          <Link to="/dashboard" className="btn-primary" data-testid="overview-cta-dashboard">Go to dashboard</Link>
        )}
      </div>
    </div>
  );
}
